/**
 * @group Functions
 *
 * @summary
 * <blockquote>Validate BaseEvent objects using Zod schema</blockquote>
 *
 * This function validates BaseEvent objects using a Zod schema to ensure
 * all required fields are present and properly formatted.
 *
 * @example
 * ```typescript
 * import { validateBaseEvent } from '@shared/reporting';
 * 
 * const event: BaseEvent = {
 *   id: 'evt-001',
 *   distinctId: 'user-123',
 *   name: 'user:sign_in',
 *   props: { method: 'credentials' }
 * };
 * 
 * const result = validateBaseEvent(event);
 * result.match({
 *   ok: (isValid) => console.log('Event is valid:', isValid),
 *   err: (error) => console.error('Validation failed:', error)
 * });
 * ```
 */
import { z } from 'zod';
import { BaseEvent } from '../types';
import { convertToAppError, AppError, err, ok, Result } from '@shared/utils';
import { VALIDATION_MESSAGES } from '../constants';
import { HTTP_STATUS_NAMES } from '@shared/constants';

// Shared base event schema with common properties
const baseEventSchema = z.object({
  id: z.string().min(1).optional(),
  distinctId: z.string().min(1),
  name: z.string().min(1),
  props: z.record(z.unknown()).optional(),
}); 

/**
 * @summary
 * <blockquote>Validates a BaseEvent object using Zod schema</blockquote>
 * 
 * @param event - The BaseEvent object to validate
 * @returns Result<boolean, AppError> indicating validation success or failure
 */
/**
 * @internal
 */
export function validateBaseEvent(event: BaseEvent): Result<boolean, AppError> {
  const result = baseEventSchema.safeParse(event);
  
  if (result.success) {
    return ok(true);
  } else {
    return err(convertToAppError(
      VALIDATION_MESSAGES.BASE_EVENT_VALIDATION_FAILED,
      VALIDATION_MESSAGES.BASE_EVENT_VALIDATION_FAILED_DETAILS,
      HTTP_STATUS_NAMES.BAD_REQUEST
    ));
  }
} 